import { FC, useCallback, useEffect, useMemo } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Navigate } from 'react-router';
import deepEquals from 'deep-equal';

import { useTranslations } from 'hooks/useTranslations';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { ICustomerUpdate } from 'models/customer';
import { PhoneNumber } from 'models/phoneNumber';
import {
  createCustomerUpdateRequest,
  getCustomerEmail,
  getCustomerPhone,
  isBusinessCustomer
} from 'utils/customerUtils/customerUtils';

import {
  customerSelector,
  resetPatchProcess,
  updateCustomer
} from 'store/customerState';
import { fetchBasicData, localizationSelector } from 'store/localizationState';

import * as routes from 'router/routes';
import Heading, { Type as HeadingType } from 'components/UI/Heading';
import Loader from 'components/UI/Loader';
import Button from 'components/UI/Buttons/Button';
import InputField from 'components/UI/InputField';
import PageWrapper from 'components/UI/Page/PageWrapper';
import SelectField from 'components/UI/SelectField';
import Copyright from 'components/UI/Copyright';
import AnimatedFormGroup from 'components/UI/AnimatedFormGroup';
import BackButton from 'components/UI/Buttons/BackButton';
import PhoneNumberField from 'components/UI/PhoneNumberField';
import Checkbox from 'components/UI/Checkbox';

import {
  Form,
  FormCol,
  FormLabel,
  FormLabelContainer,
  AdressCheckboxContainer
} from './styled';

const locationAddressSchema = z.object({
  street: z.string().optional(),
  zipCode: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional()
});

const billingAddressSchema = z.object({
  street: z.string().optional(),
  zipCode: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional()
});
type AddressType = 'locationAddress' | 'billingAddress';
type AddressValue = 'street' | 'zipCode' | 'state' | 'city';
type AddressShape = z.infer<typeof locationAddressSchema>;

function validateAddressGroup(
  ctx: z.RefinementCtx,
  path: AddressType,
  address: AddressShape
): void {
  const fields = Object.keys(
    locationAddressSchema.shape
  ) as (keyof AddressShape)[];

  const street = (address?.street ?? '').trim();
  const zipCode = (address?.zipCode ?? '').trim();
  const city = (address?.city ?? '').trim();

  const anyFilled = street !== '' || zipCode !== '' || city !== '';

  if (!anyFilled) {
    return;
  }

  for (const field of fields) {
    // state field should always be optional
    if (field === 'state') {
      continue;
    }

    const value = (address?.[field] ?? '').trim();
    if (value === '') {
      ctx.addIssue({
        path: [path, field],
        code: 'custom',
        message: 'input.required'
      });
    }
  }
}

const EditProfilePage: FC = () => {
  const t = useTranslations();
  const dispatch = useAppDispatch();

  const { customer, isLoading, completedUpdate } =
    useAppSelector(customerSelector);
  const { languageOptions, countryOptions } =
    useAppSelector(localizationSelector);

  // Check if business customer
  const isBusiness = useMemo(() => isBusinessCustomer(customer), [customer]);

  // Primary contact schema
  const primaryContactSchema = useMemo(() => {
    const schema = z.object({
      firstName: z
        .string()
        .min(1, { message: 'editprofile.form.firstname_required' }),
      lastName: z
        .string()
        .min(1, { message: 'editprofile.form.lastname_required' }),
      emailAddress: z.email({ message: 'input.email_label' }).optional(),
      phoneNumber: z
        .object({
          number: z.string().optional(),
          dialCode: z.string().optional()
        })
        .optional()
    });

    return isBusiness ? schema : schema.partial();
  }, [isBusiness]);

  const formSchema = useMemo(
    () =>
      z
        .object({
          ...(isBusiness
            ? {}
            : {
                firstName: z
                  .string()
                  .min(1, { message: 'editprofile.form.firstname_required' }),
                lastName: z
                  .string()
                  .min(1, { message: 'editprofile.form.lastname_required' })
              }),
          email: z.email({ message: 'input.email_label' }),
          language: z.string().min(1, { message: 'input.required' }),
          country: z
            .string()
            .min(1, { message: 'editprofile.form.country_required' }),
          phoneNumber: z.string().optional(),
          dialCode: z.string().optional(),
          locationAddress: locationAddressSchema,
          billingAddress: billingAddressSchema,
          sameAsLocationAddress: z.boolean(),
          primaryContact: primaryContactSchema
        })
        .superRefine((data, ctx) => {
          validateAddressGroup(ctx, 'locationAddress', data.locationAddress);

          if (!data.sameAsLocationAddress) {
            validateAddressGroup(ctx, 'billingAddress', data.billingAddress);
          }
        }),
    [isBusiness, primaryContactSchema]
  );

  const defaultSameLocationAddressValue =
    customer?.attributes.billingAddress?.street ===
      customer?.attributes.locationAddress?.street &&
    customer?.attributes.billingAddress?.zipCode ===
      customer?.attributes.locationAddress?.zipCode &&
    customer?.attributes.billingAddress?.city ===
      customer?.attributes.locationAddress?.city;

  // Phone numbers
  const phoneNumber = useMemo(
    () => PhoneNumber.parse(getCustomerPhone(customer?.attributes.eContact)),
    [customer]
  );
  const primaryContactPhoneNumber = useMemo(
    () => PhoneNumber.parse(customer?.attributes.primaryContact?.phoneNumber),
    [customer]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
    control
  } = useForm({
    mode: 'onTouched',
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: customer?.attributes.firstName,
      lastName: customer?.attributes.lastName,
      email: getCustomerEmail(customer?.attributes.eContact),
      language: customer?.attributes.language,
      country: customer?.attributes.country,
      phoneNumber: phoneNumber?.number,
      dialCode: phoneNumber?.dialCode,
      locationAddress: {
        street: customer?.attributes.locationAddress?.street,
        zipCode: customer?.attributes.locationAddress?.zipCode,
        state: customer?.attributes.locationAddress?.state,
        city: customer?.attributes.locationAddress?.city
      },
      billingAddress: {
        street: customer?.attributes.billingAddress?.street,
        zipCode: customer?.attributes.billingAddress?.zipCode,
        state: customer?.attributes.billingAddress?.state,
        city: customer?.attributes.billingAddress?.city
      },
      sameAsLocationAddress: defaultSameLocationAddressValue,
      primaryContact: {
        firstName: customer?.attributes.primaryContact?.firstName,
        lastName: customer?.attributes.primaryContact?.lastName,
        emailAddress: customer?.attributes.primaryContact?.emailAddress,
        phoneNumber: primaryContactPhoneNumber
      }
    }
  });

  // Watch - using useWatch for React 19 compiler compatibility
  const watchCountry = useWatch({
    control,
    name: 'country'
  });
  const isChecked = useWatch({
    control,
    name: 'sameAsLocationAddress'
  });

  // Reset
  useEffect(() => {
    return () => {
      dispatch(resetPatchProcess());
    };
  }, [dispatch]);

  // Fetch country and language options
  useEffect(() => {
    if (!countryOptions.length || !languageOptions.length) {
      dispatch(fetchBasicData());
    }
  }, [dispatch, countryOptions, languageOptions]);

  // Submit
  const onSubmit: SubmitHandler<any> = useCallback(
    (values) => {
      if (customer) {
        const customerUpdate: ICustomerUpdate = createCustomerUpdateRequest(
          values,
          customer.attributes.type
        );
        dispatch(updateCustomer(customer.id, customerUpdate));
      }
    },
    [dispatch, customer]
  );

  // On dial code change
  const onDialCodeChange = useCallback(
    (value: string) => setValue('dialCode', value),
    [setValue]
  );

  // On dial code change
  const onPrimaryContactDialCodeChange = useCallback(
    (value: string) => setValue('primaryContact.phoneNumber.dialCode', value),
    [setValue]
  );

  // Address error
  const addressError = useCallback(
    (type: AddressType, value: AddressValue) => errors[type]?.[value],
    [errors]
  );

  // Reset inputfield
  const reset = useCallback(
    (name: any) => {
      setValue(name, '');
      clearErrors(name);
    },
    [setValue, clearErrors]
  );

  // Render address section
  const renderAddressSection = useCallback(
    (type: AddressType, name: string) => {
      const isEqual = deepEquals(
        customer?.attributes.locationAddress,
        customer?.attributes.billingAddress
      );

      const isDefaultOpen = type === 'locationAddress' || !isEqual;
      const isToggleOpen = type === 'locationAddress' || !isChecked;
      const isOpen = isChecked === null ? isDefaultOpen : isToggleOpen;
      const disableInput = type === 'billingAddress' && isChecked;

      return (
        <>
          <FormLabelContainer>
            <FormLabel>{t(name)}</FormLabel>
          </FormLabelContainer>
          {type === 'billingAddress' && (
            <AdressCheckboxContainer>
              <Checkbox
                id="sameAsLocationAddress"
                isChecked={!isOpen}
                register={register('sameAsLocationAddress')}
              />
              {t('editprofile.form.use_location_address')}
            </AdressCheckboxContainer>
          )}
          <AnimatedFormGroup defaultIsOpen={isDefaultOpen} isOpen={isOpen}>
            <InputField
              name={`${type}.street`}
              description="editprofile.form.label_address"
              error={addressError(type, 'street')}
              register={register(`${type}.street`)}
              reset={reset}
              disabled={disableInput}
            />
            <InputField
              name={`${type}.zipCode`}
              description="editprofile.form.label_postal_code"
              error={addressError(type, 'zipCode')}
              register={register(`${type}.zipCode`)}
              reset={reset}
              disabled={disableInput}
            />
            {watchCountry === 'US' && (
              <InputField
                name={`${type}.state`}
                description="editprofile.form.label_state"
                error={addressError(type, 'state')}
                register={register(`${type}.state`)}
                reset={reset}
                disabled={disableInput}
              />
            )}
            <InputField
              name={`${type}.city`}
              description="editprofile.form.label_city"
              error={addressError(type, 'city')}
              register={register(`${type}.city`)}
              reset={reset}
              disabled={disableInput}
            />
          </AnimatedFormGroup>
        </>
      );
    },
    [addressError, register, reset, t, isChecked, customer, watchCountry]
  );

  // Primary contact section
  const primaryContactSection = useMemo(() => {
    if (!isBusiness) {
      return null;
    }

    return (
      <>
        <FormLabelContainer>
          <FormLabel>{t('editprofile.form.label_primary_contact')}</FormLabel>
        </FormLabelContainer>
        <InputField
          name={'primaryContact.firstName'}
          description="editprofile.form.label_firstname"
          error={errors.primaryContact?.firstName}
          register={register('primaryContact.firstName')}
          reset={reset}
        />
        <InputField
          name={'primaryContact.lastName'}
          description="editprofile.form.label_lastname"
          error={errors.primaryContact?.lastName}
          register={register('primaryContact.lastName')}
          reset={reset}
        />
        <InputField
          name={'primaryContact.email'}
          description="input.email_label"
          defaultValue={customer?.attributes.primaryContact?.emailAddress}
          disabled
        />
        <PhoneNumberField
          description="editprofile.form.label_phone_number"
          numberInputRef={register('primaryContact.phoneNumber.number')}
          dialCodeInputRef={register('primaryContact.phoneNumber.dialCode')}
          onDialCodeChange={onPrimaryContactDialCodeChange}
          defaultDialCode={primaryContactPhoneNumber?.dialCode}
          defaultNumber={primaryContactPhoneNumber?.number}
          error={errors.primaryContact?.phoneNumber}
        />
      </>
    );
  }, [
    isBusiness,
    customer,
    errors,
    primaryContactPhoneNumber,
    onPrimaryContactDialCodeChange,
    register,
    reset,
    t
  ]);

  if (completedUpdate) {
    return <Navigate to={routes.PROFILE} />;
  }

  if (
    !customer ||
    isLoading ||
    !languageOptions.length ||
    !countryOptions.length
  ) {
    return (
      <PageWrapper>
        <Loader center size="large" />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper responsive={false}>
      <Copyright>
        <BackButton backUrl={routes.PROFILE}>
          {t('productinfo.btn_back')}
        </BackButton>
        <Heading type={HeadingType.h1}>{t('editprofile.meta_title')}</Heading>
        <Form onSubmit={handleSubmit(onSubmit)}>
          {!isBusiness && (
            <FormCol>
              <InputField
                name="firstName"
                description="editprofile.form.label_firstname"
                error={errors.firstName}
                register={register('firstName')}
                reset={reset}
              />
              <InputField
                name="lastName"
                description="editprofile.form.label_lastname"
                error={errors.lastName}
                register={register('lastName')}
                reset={reset}
              />
            </FormCol>
          )}
          <InputField
            name="email"
            description="input.email_label"
            defaultValue={getCustomerEmail(customer?.attributes.eContact)}
            disabled
          />
          <PhoneNumberField
            description="editprofile.form.label_phone_number"
            numberInputRef={register('phoneNumber')}
            dialCodeInputRef={register('dialCode')}
            onDialCodeChange={onDialCodeChange}
            defaultDialCode={phoneNumber.dialCode}
            defaultNumber={phoneNumber.number}
            error={errors.phoneNumber}
          />
          <SelectField
            name="language"
            register={register('language')}
            setValue={setValue}
            clearErrors={clearErrors}
            defaultValue={customer.attributes.language}
            placeholder={t('editprofile.form.label_language_prompt')}
            description="editprofile.form.label_language"
            options={languageOptions}
            error={errors.language}
          />
          <SelectField
            name="country"
            register={register('country')}
            setValue={setValue}
            clearErrors={clearErrors}
            placeholder={t('editprofile.form.label_country_prompt')}
            description="editprofile.form.label_country"
            options={countryOptions}
            defaultValue={customer.attributes.country}
            error={errors.country}
          />
          {renderAddressSection(
            'locationAddress',
            'editprofile.form.label_location_address'
          )}
          {renderAddressSection(
            'billingAddress',
            'editprofile.form.label_billing_address'
          )}
          {primaryContactSection}
          <Button submit fullWidth>
            {t('editprofile.form.btn_submit')}
          </Button>
        </Form>
      </Copyright>
    </PageWrapper>
  );
};

export default EditProfilePage;
